import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import {
  MessageSquare, Send, ArrowLeft, Search, CheckCheck, Check, X,
  Flag, Info, User, Paperclip, Image as ImageIcon, FileText, Download,
  Pencil, ArrowDown, Loader2, FolderPlus
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { productLabel } from '../applications/loanFieldConstants';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// 5-minute hard edit window (must match EDIT_WINDOW_SECONDS in backend).
const EDIT_WINDOW_MS = 5 * 60 * 1000;

const linkifyContent = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer"
        className="underline font-medium break-all hover:opacity-80">{part}</a>
    ) : part
  );
};

const formatBytes = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

// ATTACHMENT bubble — image preview if it's an image, otherwise file card with download link.
const AttachmentItem = ({ att, isOwn }) => {
  // Use a SAME-ORIGIN relative URL so the link works regardless of which env
  // the frontend was built against. The Kubernetes ingress already routes
  // /api/* to the backend on the current host. Bearer-token auth is passed as
  // ?token=... because <img> and <a download> can't send Authorization headers.
  const authToken = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : '';
  const url = `/api/messages/attachment/${att.file_id}${authToken ? `?token=${encodeURIComponent(authToken)}` : ''}`;
  const savedBadge = att.saved_to?.target_name ? (
    <span
      className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
        isOwn ? 'bg-white/20 text-white' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
      }`}
      title={`Also filed in application documents as: ${att.saved_to.target_name}`}
      data-testid={`msg-attachment-saved-${att.file_id}`}
    >
      <Check className="h-2.5 w-2.5" /> Filed: {att.saved_to.target_name}
    </span>
  ) : null;

  if (att.is_image) {
    return (
      <div className="mt-2">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-lg overflow-hidden border border-slate-200 max-w-[280px] hover:opacity-95"
          data-testid={`msg-attachment-image-${att.file_id}`}
        >
          <img
            src={url}
            alt={att.filename}
            className="block w-full h-auto max-h-[300px] object-cover"
            loading="lazy"
          />
        </a>
        {savedBadge}
      </div>
    );
  }
  // Non-image file: download tile
  return (
    <div className="mt-2">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        download={att.filename}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border max-w-[280px] hover:opacity-90 transition-opacity ${
          isOwn ? 'bg-white/10 border-white/20 text-white' : 'bg-slate-50 border-slate-200 text-slate-800'
        }`}
        data-testid={`msg-attachment-file-${att.file_id}`}
      >
        <FileText className={`h-4 w-4 flex-shrink-0 ${isOwn ? 'text-white/80' : 'text-slate-500'}`} />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium truncate">{att.filename}</div>
          <div className={`text-[10px] ${isOwn ? 'text-white/70' : 'text-slate-500'}`}>{formatBytes(att.size)}</div>
        </div>
        <Download className={`h-3.5 w-3.5 flex-shrink-0 ${isOwn ? 'text-white/80' : 'text-slate-500'}`} />
      </a>
      {savedBadge}
    </div>
  );
};

const MessagingPanel = ({ userRole, userId, preSelectedAppId, onAppSelect, onBack }) => {
  const { ws } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeChannel, setActiveChannel] = useState(
    userRole === 'borrower' ? 'underwriter_borrower' : 'broker_underwriter'
  );
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [markImportant, setMarkImportant] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [messageFilter, setMessageFilter] = useState('user');

  // Pending attachments — list of {file_id, filename, content_type, size, is_image, uploading?, saved_to?}
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  // Doc slots fetched once when the user opens the save picker (or when a conversation loads)
  const [appDocSlots, setAppDocSlots] = useState([]);
  const [savePickerOpenFor, setSavePickerOpenFor] = useState(null);
  const [savingTo, setSavingTo] = useState(false);

  // Edit-in-place state
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  // forces a re-render once per 30s so the "edit" affordance disappears when the 5-min window ends
  const [, setNowTick] = useState(Date.now());

  // Scroll tracking — only auto-scroll when the user is at the bottom OR they just sent.
  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const justSentRef = useRef(false);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  const getAccessibleChannels = useCallback(() => {
    if (userRole === 'admin' || userRole === 'underwriter' || userRole === 'jr_underwriter') return ['broker_underwriter', 'underwriter_borrower'];
    if (userRole === 'broker') return ['broker_underwriter'];
    if (userRole === 'borrower') return ['underwriter_borrower'];
    return [];
  }, [userRole]);

  const accessibleChannels = getAccessibleChannels();

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/messages/conversations`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!selectedConversation) return;
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/messages/app/${selectedConversation.application_id}?channel=${activeChannel}`
      );
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [selectedConversation, activeChannel]);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  useEffect(() => {
    if (preSelectedAppId && conversations.length > 0) {
      const conv = conversations.find(c => c.application_id === preSelectedAppId);
      if (conv) {
        setSelectedConversation(conv);
      } else {
        setSelectedConversation({ application_id: preSelectedAppId, business_name: 'Application', unread_count: 0 });
      }
    }
  }, [preSelectedAppId, conversations]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 15000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation, activeChannel, fetchMessages]);

  useEffect(() => {
    if (!ws) return;
    const removeListener = ws.addListener((data) => {
      if (data.type === 'new_message') {
        const msg = data.message;
        if (selectedConversation && msg.application_id === selectedConversation.application_id && msg.channel === activeChannel) {
          setMessages(prev => {
            if (prev.some(m => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
          if (msg.sender_id !== userId) {
            ws.sendMessage({ type: 'mark_read', message_id: msg.id });
          }
        }
        fetchConversations();
      } else if (data.type === 'message_edited') {
        const msg = data.message;
        if (selectedConversation && msg.application_id === selectedConversation.application_id) {
          setMessages(prev => prev.map(m => (m.id === msg.id ? { ...m, ...msg } : m)));
        }
      }
    });
    return removeListener;
  }, [ws, selectedConversation, activeChannel, userId, fetchConversations]);

  // Auto-scroll behavior:
  //   - if the user just sent a message → snap to bottom
  //   - if the user was already at the bottom → keep them at the bottom (good)
  //   - otherwise (they scrolled up to read history) → DO NOT scroll;
  //     just show a "Jump to latest" pill so they can opt-in.
  useEffect(() => {
    if (justSentRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      justSentRef.current = false;
      setShowJumpToLatest(false);
      return;
    }
    if (isAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setShowJumpToLatest(false);
    } else if (messages.length > 0) {
      setShowJumpToLatest(true);
    }
  }, [messages]);

  // Track whether the user is sitting at (or near) the bottom of the scroll
  // container. We use a small 80px slack so common minor scrolls still count
  // as "at the bottom".
  const handleScroll = useCallback(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    isAtBottomRef.current = dist < 80;
    if (isAtBottomRef.current) setShowJumpToLatest(false);
  }, []);

  // Tick once a minute so messages cross the 5-min edit threshold lose their
  // edit button without needing a page refresh.
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (selectedConversation && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [selectedConversation]);

  const sendMessage = async (e) => {
    e.preventDefault();
    const hasText = newMessage.trim().length > 0;
    const hasAttachments = pendingAttachments.length > 0;
    if (!selectedConversation) return;
    if (!hasText && !hasAttachments) return;
    if (uploadingAttachment) return;  // wait for upload to finish first
    setSending(true);
    try {
      // Strip transient/UI-only fields off the attachments before sending
      const cleanAttachments = pendingAttachments.map(({ uploading, ...keep }) => keep);
      await axios.post(`${BACKEND_URL}/api/messages/send`, {
        application_id: selectedConversation.application_id,
        channel: activeChannel,
        content: newMessage,
        is_important: markImportant,
        attachments: cleanAttachments,
      });
      setNewMessage('');
      setPendingAttachments([]);
      setMarkImportant(false);
      justSentRef.current = true;
      fetchMessages();
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      alert(error?.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Open the native file picker (multiple files allowed)
  const triggerFilePicker = () => fileInputRef.current?.click();

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || !selectedConversation) return;
    setUploadingAttachment(true);
    try {
      for (const file of files) {
        const placeholderId = `tmp-${Date.now()}-${file.name}`;
        setPendingAttachments(prev => [...prev, {
          file_id: placeholderId,
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          size: file.size,
          is_image: (file.type || '').startsWith('image/'),
          uploading: true,
        }]);
        try {
          const fd = new FormData();
          fd.append('file', file);
          const res = await axios.post(
            `${BACKEND_URL}/api/messages/upload?application_id=${selectedConversation.application_id}`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          setPendingAttachments(prev => prev.map(a => a.file_id === placeholderId ? res.data : a));
        } catch (err) {
          setPendingAttachments(prev => prev.filter(a => a.file_id !== placeholderId));
          alert(err?.response?.data?.detail || `Failed to upload ${file.name}`);
        }
      }
    } finally {
      setUploadingAttachment(false);
    }
  };

  const removePendingAttachment = (file_id) => {
    setPendingAttachments(prev => prev.filter(a => a.file_id !== file_id));
  };

  // Load required-doc slots for the open application so the "Save to docs"
  // picker can offer real slot names. Falls back to a generic list if the
  // application has no slots configured yet.
  const ensureDocSlotsLoaded = useCallback(async () => {
    if (!selectedConversation?.application_id) return;
    if (appDocSlots.length > 0) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/applications/${selectedConversation.application_id}`);
      const slots = (res.data?.required_documents || []).map(d => d.name).filter(Boolean);
      setAppDocSlots(slots);
    } catch (err) {
      setAppDocSlots([]);
    }
  }, [selectedConversation, appDocSlots.length]);

  const openSavePicker = async (file_id) => {
    if (savePickerOpenFor === file_id) {
      setSavePickerOpenFor(null);
      return;
    }
    await ensureDocSlotsLoaded();
    setSavePickerOpenFor(file_id);
  };

  const saveAttachmentToDocs = async (attachment, targetName) => {
    if (!selectedConversation?.application_id) return;
    setSavingTo(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/applications/${selectedConversation.application_id}/documents/save-from-attachment`,
        { file_id: attachment.file_id, target_name: targetName }
      );
      setPendingAttachments(prev => prev.map(a => (
        a.file_id === attachment.file_id
          ? { ...a, saved_to: { target_name: res.data.target_name, target_type: res.data.target_type } }
          : a
      )));
      setSavePickerOpenFor(null);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save to documents');
    } finally {
      setSavingTo(false);
    }
  };

  // -------- Edit message --------
  const canEditMessage = useCallback((msg) => {
    if (!msg || msg.sender_id !== userId) return false;
    if (msg.sender_role === 'system') return false;
    const created = new Date(msg.created_at).getTime();
    if (!created || Number.isNaN(created)) return false;
    return Date.now() - created < EDIT_WINDOW_MS;
  }, [userId]);

  const startEdit = (msg) => {
    setEditingId(msg.id);
    setEditDraft(msg.content || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const saveEdit = async (msg) => {
    const trimmed = editDraft.trim();
    if (!trimmed && (!msg.attachments || msg.attachments.length === 0)) {
      alert('Message cannot be empty');
      return;
    }
    if (trimmed === (msg.content || '')) { cancelEdit(); return; }
    try {
      const res = await axios.put(`${BACKEND_URL}/api/messages/${msg.id}/edit`, { content: trimmed });
      const updated = res.data?.message;
      if (updated) {
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, ...updated } : m));
      }
      cancelEdit();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to edit message');
    }
  };

  const toggleImportant = async (messageId, currentVal) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/messages/${messageId}/important`);
      if (res.data.success) {
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_important: res.data.is_important } : m));
      }
    } catch (error) {
      console.error('Error toggling important:', error);
    }
  };

  const getChannelLabel = (channel) => {
    if (channel === 'broker_underwriter') return 'Broker Channel';
    if (channel === 'underwriter_borrower') return 'Borrower Channel';
    return channel;
  };

  const getRoleBadgeStyle = (role) => {
    const styles = {
      broker: 'bg-blue-600 text-white',
      borrower: 'bg-emerald-600 text-white',
      underwriter: 'bg-violet-600 text-white',
      admin: 'bg-amber-600 text-white',
      system: 'bg-slate-400 text-white'
    };
    return styles[role] || 'bg-slate-400 text-white';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredConversations = conversations.filter(conv => {
    // Filter by message type (user vs system)
    const isSystemConv = conv.last_sender_role === 'system';
    if (messageFilter === 'user' && isSystemConv) return false;
    if (messageFilter === 'system' && !isSystemConv) return false;

    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (conv.business_name || '').toLowerCase().includes(q) ||
           (conv.last_message || '').toLowerCase().includes(q) ||
           (conv.product_type || '').toLowerCase().includes(q);
  });

  const userCount = conversations.filter(c => c.last_sender_role !== 'system').length;
  const systemCount = conversations.filter(c => c.last_sender_role === 'system').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  // ---- Message Thread View ----
  if (selectedConversation) {
    return (
      <div className="flex flex-col h-[calc(100vh-180px)] min-h-[680px]" data-testid="message-thread">
        {/* Thread Header */}
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-200">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setSelectedConversation(null); if (onAppSelect) onAppSelect(null); }}
            className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100 hover:border-slate-400 font-semibold gap-1.5"
            data-testid="back-to-conversations"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-800 truncate">{selectedConversation.business_name}</h3>
            {selectedConversation.product_type && (
              <p className="text-xs text-slate-500">{productLabel(selectedConversation.product_type).toUpperCase()}</p>
            )}
          </div>
          {/* Legend */}
          <div className="hidden sm:flex items-center gap-3 text-[10px] text-slate-400">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300 inline-block" />System</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#0E333F] inline-block" />You</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-white border border-slate-300 inline-block" />Others</span>
          </div>
        </div>

        {/* Channel Tabs */}
        {accessibleChannels.length > 1 && (
          <Tabs value={activeChannel} onValueChange={setActiveChannel} className="mb-3">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="broker_underwriter" data-testid="channel-broker-uw">Broker Channel</TabsTrigger>
              <TabsTrigger value="underwriter_borrower" data-testid="channel-uw-borrower">Borrower Channel</TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {/* Messages */}
        <div
          ref={messagesScrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto space-y-3 p-4 bg-gradient-to-b from-slate-50 to-slate-100/60 rounded-xl border border-slate-200 relative"
          data-testid="messages-scroll-container"
        >
          {messages.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <MessageSquare className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-sm">No messages yet</p>
              <p className="text-xs mt-1 text-slate-400">Start a conversation in {getChannelLabel(activeChannel)}</p>
            </div>
          ) : (
            messages.map((msg, idx) => {
              const isOwn = msg.sender_id === userId;
              const isSystem = msg.sender_role === 'system';
              const isRead = (msg.read_by || []).length > 1;
              const isImportant = msg.is_important;
              const showDateSep = idx === 0 || new Date(messages[idx-1]?.created_at).toDateString() !== new Date(msg.created_at).toDateString();

              return (
                <React.Fragment key={msg.id}>
                  {showDateSep && (
                    <div className="flex items-center gap-3 my-4">
                      <div className="flex-1 h-px bg-slate-200" />
                      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest bg-slate-100 px-3 py-1 rounded-full">
                        {new Date(msg.created_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                      <div className="flex-1 h-px bg-slate-200" />
                    </div>
                  )}

                  {/* === SYSTEM MESSAGE === */}
                  {isSystem ? (
                    <div className="flex justify-center my-2" data-testid={`system-message-${msg.id}`}>
                      <div className={`flex items-center gap-2 text-xs text-slate-500 bg-slate-100/80 border border-slate-200 px-4 py-2 rounded-full italic ${isImportant ? 'animate-important-glow !border-yellow-400 !bg-yellow-50/60 !text-yellow-800' : ''}`}>
                        <Info className="h-3 w-3 text-slate-400 flex-shrink-0" />
                        <span>{msg.content}</span>
                        <button
                          onClick={() => toggleImportant(msg.id, isImportant)}
                          className={`ml-1 p-0.5 rounded transition-colors ${isImportant ? 'text-yellow-600 hover:text-yellow-700' : 'text-slate-300 hover:text-yellow-500'}`}
                          title={isImportant ? 'Remove important flag' : 'Flag as important'}
                          data-testid={`flag-important-${msg.id}`}
                        >
                          <Flag className="h-3 w-3" fill={isImportant ? 'currentColor' : 'none'} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* === USER MESSAGE === */
                    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`} data-testid={`message-${msg.id}`}>
                      {/* Avatar for incoming messages */}
                      {!isOwn && (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center flex-shrink-0 mr-2 mt-1 shadow-sm">
                          <User className="h-3.5 w-3.5 text-slate-500" />
                        </div>
                      )}
                      <div className={`max-w-[72%] relative ${isImportant ? 'animate-important-glow rounded-2xl' : ''}`}>
                        <div className={`px-4 py-3 rounded-2xl shadow-sm ${
                          isOwn
                            ? 'bg-gradient-to-br from-[#0E333F] to-[#163d4a] text-white rounded-br-md'
                            : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md shadow-md'
                        }`}>
                          {/* Sender info for incoming */}
                          {!isOwn && (
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-[13px] font-bold text-slate-800">{msg.sender_name || msg.sender_role}</span>
                              {msg.sender_company && (
                                <span className="text-[11px] text-slate-500 font-medium">{msg.sender_company}</span>
                              )}
                              <Badge className={`text-[9px] px-1.5 py-0 leading-tight font-semibold rounded-md ${getRoleBadgeStyle(msg.sender_role)}`}>
                                {msg.sender_role}
                              </Badge>
                            </div>
                          )}
                          {/* Message body — inline edit if this is the one being edited */}
                          {editingId === msg.id ? (
                            <div className="space-y-2" data-testid={`edit-form-${msg.id}`}>
                              <textarea
                                value={editDraft}
                                onChange={(e) => setEditDraft(e.target.value)}
                                rows={3}
                                autoFocus
                                className="w-full text-sm rounded-md border border-slate-300 text-slate-800 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#66C2CA]/40"
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') cancelEdit();
                                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveEdit(msg);
                                }}
                                data-testid={`edit-input-${msg.id}`}
                              />
                              <div className="flex justify-end gap-1.5">
                                <Button size="sm" variant="ghost" onClick={cancelEdit} className={isOwn ? 'text-white/80 hover:bg-white/10 h-7 text-xs' : 'text-slate-600 h-7 text-xs'} data-testid={`edit-cancel-${msg.id}`}>Cancel</Button>
                                <Button size="sm" onClick={() => saveEdit(msg)} className="bg-white text-[#0E333F] hover:bg-slate-100 h-7 text-xs" data-testid={`edit-save-${msg.id}`}>Save</Button>
                              </div>
                              <p className={`text-[10px] italic ${isOwn ? 'text-white/60' : 'text-slate-400'}`}>Esc to cancel · Ctrl/Cmd+Enter to save</p>
                            </div>
                          ) : (
                            <>
                              {msg.content && (
                                <p className={`whitespace-pre-wrap leading-relaxed ${isOwn ? 'text-sm' : 'text-[14px]'}`}>
                                  {linkifyContent(msg.content)}
                                </p>
                              )}
                              {/* Attachments */}
                              {(msg.attachments || []).map(a => (
                                <AttachmentItem key={a.file_id} att={a} isOwn={isOwn} />
                              ))}
                            </>
                          )}
                          {/* Timestamp + (edited) + read receipt + flag + edit */}
                          <div className={`flex items-center gap-1.5 mt-2 ${isOwn ? 'justify-end' : 'justify-between'}`}>
                            <span className={`text-[10px] ${isOwn ? 'text-white/60' : 'text-slate-400'}`}>
                              {new Date(msg.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                              {msg.edited_at && (
                                <span className="ml-1 italic" data-testid={`edited-tag-${msg.id}`}>(edited)</span>
                              )}
                            </span>
                            <div className="flex items-center gap-1">
                              {/* Edit button — only your own messages within 5min, only when not already editing */}
                              {editingId !== msg.id && canEditMessage(msg) && (
                                <button
                                  onClick={() => startEdit(msg)}
                                  className={`p-0.5 rounded transition-all opacity-0 group-hover:opacity-100 ${isOwn ? 'text-white/70 hover:text-white' : 'text-slate-400 hover:text-slate-700'}`}
                                  title="Edit message"
                                  data-testid={`edit-message-${msg.id}`}
                                >
                                  <Pencil className="h-3 w-3" />
                                </button>
                              )}
                              {/* Flag button */}
                              <button
                                onClick={() => toggleImportant(msg.id, isImportant)}
                                className={`p-0.5 rounded transition-all opacity-0 group-hover:opacity-100 ${isImportant ? '!opacity-100 text-yellow-500 hover:text-yellow-600' : (isOwn ? 'text-white/70 hover:text-yellow-300' : 'text-slate-300 hover:text-yellow-500')}`}
                                title={isImportant ? 'Remove important flag' : 'Flag as important'}
                                data-testid={`flag-important-${msg.id}`}
                              >
                                <Flag className="h-3 w-3" fill={isImportant ? 'currentColor' : 'none'} />
                              </button>
                              {isOwn && (
                                isRead
                                  ? <CheckCheck className="h-3.5 w-3.5 text-blue-300" />
                                  : <Check className="h-3.5 w-3.5 text-slate-400" />
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Important indicator bar */}
                        {isImportant && (
                          <div className="absolute -left-1 top-2 bottom-2 w-1 bg-yellow-400 rounded-full" />
                        )}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Jump to latest pill — appears when user scrolled up and new messages arrived */}
        {showJumpToLatest && (
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                setShowJumpToLatest(false);
              }}
              className="absolute -top-12 left-1/2 -translate-x-1/2 z-10 bg-[#0E333F] hover:bg-[#081120] text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 animate-in fade-in slide-in-from-bottom-2"
              data-testid="jump-to-latest"
            >
              <ArrowDown className="h-3.5 w-3.5" /> Jump to latest
            </button>
          </div>
        )}

        {/* Pending attachments preview row */}
        {pendingAttachments.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="pending-attachments">
            {pendingAttachments.map((a) => (
              <div
                key={a.file_id}
                className="relative group bg-white border border-slate-200 rounded-lg pl-2 pr-7 py-1.5 flex items-center gap-2 text-xs shadow-sm"
                data-testid={`pending-attachment-${a.file_id}`}
              >
                {a.uploading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500" />
                ) : a.is_image ? (
                  <ImageIcon className="h-3.5 w-3.5 text-slate-500" />
                ) : (
                  <FileText className="h-3.5 w-3.5 text-slate-500" />
                )}
                <span className="text-slate-700 max-w-[180px] truncate font-medium">{a.filename}</span>
                <span className="text-slate-400">{formatBytes(a.size)}</span>

                {/* Saved-to-docs badge OR the picker trigger */}
                {a.saved_to ? (
                  <span
                    className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded text-[10px] font-medium max-w-[140px]"
                    title={`Filed in application docs as: ${a.saved_to.target_name}`}
                    data-testid={`pending-attachment-saved-${a.file_id}`}
                  >
                    <Check className="h-2.5 w-2.5" />
                    <span className="truncate">Saved: {a.saved_to.target_name}</span>
                  </span>
                ) : !a.uploading && (
                  <button
                    type="button"
                    onClick={() => openSavePicker(a.file_id)}
                    className="flex items-center gap-1 px-1.5 py-0.5 border border-slate-200 hover:border-[#0E333F] hover:text-[#0E333F] text-slate-500 rounded text-[10px] font-medium"
                    title="Also file this in the application's documents"
                    data-testid={`pending-attachment-save-btn-${a.file_id}`}
                  >
                    <FolderPlus className="h-3 w-3" />
                    Save to docs
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => removePendingAttachment(a.file_id)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500 p-0.5 rounded"
                  title="Remove"
                  data-testid={`pending-attachment-remove-${a.file_id}`}
                >
                  <X className="h-3 w-3" />
                </button>

                {/* Save picker popover */}
                {savePickerOpenFor === a.file_id && (
                  <div
                    className="absolute z-50 left-0 bottom-full mb-2 w-72 bg-white border border-slate-200 rounded-lg shadow-xl p-2"
                    data-testid={`pending-attachment-save-picker-${a.file_id}`}
                  >
                    <div className="flex items-center justify-between px-2 pt-1 pb-2 border-b border-slate-100 mb-1.5">
                      <span className="text-xs font-bold text-slate-700">File under…</span>
                      <button
                        onClick={() => setSavePickerOpenFor(null)}
                        className="text-slate-400 hover:text-slate-700 p-0.5"
                        title="Close"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="max-h-56 overflow-y-auto space-y-0.5">
                      {appDocSlots.length === 0 ? (
                        <p className="text-xs italic text-slate-400 px-2 py-2">No required-doc slots set up yet — use Additional Documents below.</p>
                      ) : (
                        appDocSlots.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => saveAttachmentToDocs(a, slot)}
                            disabled={savingTo}
                            className="w-full text-left px-2 py-1.5 rounded text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                            data-testid={`pending-attachment-save-slot-${a.file_id}-${slot.slice(0, 30).replace(/\s/g, '-').toLowerCase()}`}
                          >
                            {slot}
                          </button>
                        ))
                      )}
                    </div>
                    <div className="border-t border-slate-100 mt-1.5 pt-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          const custom = window.prompt('Save under additional document name:', a.filename.replace(/\.[^.]+$/, ''));
                          if (custom && custom.trim()) saveAttachmentToDocs(a, custom.trim());
                        }}
                        disabled={savingTo}
                        className="w-full text-left px-2 py-1.5 rounded text-xs text-[#0E333F] hover:bg-slate-100 font-semibold flex items-center gap-1.5 disabled:opacity-50"
                        data-testid={`pending-attachment-save-additional-${a.file_id}`}
                      >
                        <FolderPlus className="h-3 w-3" /> Save as Additional Document…
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Input */}
        <form onSubmit={sendMessage} className="mt-3 flex gap-2 items-end" data-testid="message-compose-form">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFilesSelected}
            className="hidden"
            data-testid="message-file-input"
          />
          <button
            type="button"
            onClick={triggerFilePicker}
            disabled={uploadingAttachment || sending}
            className="h-[60px] w-[60px] flex items-center justify-center rounded-lg border-2 transition-all flex-shrink-0 bg-white border-slate-200 text-slate-400 hover:text-[#0E333F] hover:border-[#0E333F]/30 disabled:opacity-50"
            title="Attach files"
            data-testid="attach-file-btn"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          <textarea
            ref={inputRef}
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 260) + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!sending && !uploadingAttachment) sendMessage(e);
              }
            }}
            placeholder={`Message in ${getChannelLabel(activeChannel)}…`}
            className="flex-1 text-sm text-slate-900 placeholder:text-slate-400 border border-slate-200 rounded-lg px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-[#66C2CA]/40 focus:border-transparent leading-relaxed"
            disabled={sending}
            rows={3}
            style={{ minHeight: '80px', maxHeight: '260px' }}
            data-testid="message-input"
          />
          <button
            type="button"
            onClick={() => setMarkImportant(!markImportant)}
            className={`h-[60px] w-[60px] flex items-center justify-center rounded-lg border-2 transition-all flex-shrink-0 ${
              markImportant
                ? 'bg-yellow-100 border-yellow-400 text-yellow-600'
                : 'bg-white border-slate-200 text-slate-300 hover:text-yellow-500 hover:border-yellow-300'
            }`}
            title={markImportant ? 'Message will be sent as Important' : 'Mark as Important'}
            data-testid="toggle-important-send"
          >
            <Flag className="h-5 w-5" fill={markImportant ? 'currentColor' : 'none'} />
          </button>
          <Button
            type="submit"
            disabled={(!newMessage.trim() && pendingAttachments.length === 0) || sending || uploadingAttachment}
            className="bg-[#0E333F] hover:bg-[#081120] h-[60px] w-[60px] flex-shrink-0"
            data-testid="send-message-btn"
          >
            <Send className="h-5 w-5" />
          </Button>
        </form>
        {markImportant && (
          <p className="text-[10px] text-yellow-600 font-medium mt-1 ml-1">This message will be flagged as Important</p>
        )}
        <p className="text-[10px] text-slate-400 mt-1 ml-1">Shift + Enter for new line</p>
      </div>
    );
  }

  // ---- Conversations List View ----
  return (
    <div data-testid="conversations-list">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {onBack && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="border-2 border-slate-300 text-slate-600 hover:bg-slate-100 hover:border-slate-400 font-semibold gap-1.5"
              data-testid="back-from-messages"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Messages
            {conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0) > 0 && (
              <Badge className="bg-red-500 text-white text-xs ml-1">
                {conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0)}
              </Badge>
            )}
          </h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowSearch(!showSearch)}
          className={showSearch ? 'text-slate-800' : 'text-slate-400'}
          data-testid="toggle-search"
        >
          {showSearch ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>

      {showSearch && (
        <div className="mb-3">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations..."
            className="w-full"
            autoFocus
            data-testid="conversation-search"
          />
        </div>
      )}

      {/* User / System message filter tabs */}
      <div className="flex gap-1 mb-4 bg-slate-100 p-1 rounded-lg" data-testid="message-filter-tabs">
        <button
          onClick={() => setMessageFilter('user')}
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
            messageFilter === 'user'
              ? 'bg-white text-slate-800 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="filter-user-messages"
        >
          <User className="h-4 w-4" />
          User Messages
          {userCount > 0 && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${messageFilter === 'user' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-500'}`}>
              {userCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setMessageFilter('system')}
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
            messageFilter === 'system'
              ? 'bg-white text-slate-800 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="filter-system-messages"
        >
          <Info className="h-4 w-4" />
          System
          {systemCount > 0 && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${messageFilter === 'system' ? 'bg-slate-200 text-slate-700' : 'bg-slate-200 text-slate-500'}`}>
              {systemCount}
            </span>
          )}
        </button>
      </div>

      {filteredConversations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            {searchQuery ? (
              <>
                <h3 className="text-sm font-medium text-slate-700 mb-1">No results for "{searchQuery}"</h3>
                <p className="text-xs text-slate-400">Try a different search term</p>
              </>
            ) : (
              <>
                <h3 className="text-sm font-medium text-slate-700 mb-1">No conversations yet</h3>
                <p className="text-xs text-slate-400">Messages will appear here when conversations start on applications.</p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1.5">
          {filteredConversations.map((conv) => {
            const isSystemLast = conv.last_sender_role === 'system';
            const senderRole = conv.last_sender_role || '';
            const hasImportant = conv.has_important;

            // Full-row background + accent by role — strong tints
            const roleStyles = {
              system:      { bg: 'bg-slate-200/80', hoverBg: 'hover:bg-slate-300/70', iconBg: 'bg-slate-400',     iconColor: 'text-white',      roleBadge: 'bg-slate-500 text-white' },
              broker:      { bg: 'bg-blue-100',     hoverBg: 'hover:bg-blue-200/80',  iconBg: 'bg-blue-500',      iconColor: 'text-white',      roleBadge: 'bg-blue-600 text-white' },
              underwriter: { bg: 'bg-violet-100',   hoverBg: 'hover:bg-violet-200/80', iconBg: 'bg-violet-500',   iconColor: 'text-white',      roleBadge: 'bg-violet-600 text-white' },
              admin:       { bg: 'bg-amber-100',    hoverBg: 'hover:bg-amber-200/80',  iconBg: 'bg-amber-500',    iconColor: 'text-white',      roleBadge: 'bg-amber-600 text-white' },
              borrower:    { bg: 'bg-emerald-100',  hoverBg: 'hover:bg-emerald-200/80', iconBg: 'bg-emerald-500', iconColor: 'text-white',      roleBadge: 'bg-emerald-600 text-white' },
            };
            const style = roleStyles[senderRole] || roleStyles.system;

            return (
              <div
                key={conv.application_id}
                className={`flex items-center gap-3 p-3.5 rounded-xl cursor-pointer transition-all border ${style.bg} ${style.hoverBg} ${
                  conv.unread_count > 0 ? 'border-slate-300 shadow-md' : 'border-transparent'
                } ${hasImportant ? 'animate-attention-pulse' : ''}`}
                onClick={() => { setSelectedConversation(conv); if (onAppSelect) onAppSelect(conv.application_id); }}
                data-testid={`conversation-${conv.application_id}`}
              >
                {/* Icon — different for system vs user */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${style.iconBg}`}>
                  {isSystemLast
                    ? <Info className={`h-4 w-4 ${style.iconColor}`} />
                    : <User className={`h-4 w-4 ${style.iconColor}`} />
                  }
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <h4 className={`text-sm truncate ${conv.unread_count > 0 ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>
                        {conv.business_name}
                      </h4>
                      {hasImportant && <Flag className="h-3 w-3 text-yellow-500 flex-shrink-0" fill="currentColor" />}
                    </div>
                    <span className="text-[10px] text-slate-400 whitespace-nowrap ml-2">{formatTime(conv.last_message_time)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Badge className={`text-[8px] px-1.5 py-0 leading-tight font-semibold rounded-md flex-shrink-0 ${style.roleBadge}`}>
                        {isSystemLast ? 'SYSTEM' : senderRole.toUpperCase()}
                      </Badge>
                      {!isSystemLast && conv.last_sender_name && (
                        <span className="text-[10px] font-semibold text-slate-600 flex-shrink-0 truncate max-w-[100px]">
                          {conv.last_sender_name}{conv.last_sender_company ? ` · ${conv.last_sender_company}` : ''}:
                        </span>
                      )}
                      <p className={`text-xs truncate ${conv.unread_count > 0 ? 'text-slate-800 font-semibold' : isSystemLast ? 'text-slate-400 italic' : 'text-slate-600'}`}>
                        {conv.last_message || 'No messages'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                      <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                        {(conv.product_type || '').replace('_', ' ')}
                      </Badge>
                      {conv.unread_count > 0 && (
                        <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`unread-badge-${conv.application_id}`}>
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MessagingPanel;
